import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";

const ShopContext = createContext(null);

const save = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked (iframe / privacy mode) – keep state in memory only */
  }
};

const load = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

// Guard against stale/corrupted localStorage (prevents a persistent white screen)
const loadList = (key) => {
  const v = load(key, []);
  return Array.isArray(v) ? v.filter((i) => i && typeof i === "object" && typeof i.slug === "string") : [];
};
const loadUser = () => {
  const v = load("cc_user", null);
  return v && typeof v === "object" && typeof v.email === "string" ? v : null;
};

export const ShopProvider = ({ children }) => {
  const [cart, setCart] = useState(() => loadList("cc_cart").filter((i) => typeof i.price === "number" && typeof i.qty === "number"));
  const [wishlist, setWishlist] = useState(() => loadList("cc_wishlist"));
  const [compare, setCompare] = useState(() => loadList("cc_compare"));
  const [user, setUser] = useState(loadUser);
  const [miniCartOpen, setMiniCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    save("cc_cart", cart);
  }, [cart]);
  useEffect(() => {
    save("cc_wishlist", wishlist);
  }, [wishlist]);
  useEffect(() => {
    save("cc_compare", compare);
  }, [compare]);
  useEffect(() => {
    save("cc_user", user);
  }, [user]);

  const addToCart = useCallback((product, variant = null, qty = 1) => {
    const key = `${product.slug}::${variant || ""}`;
    toast.success("Adăugat în coș", { description: `${product.name}${variant ? ` · ${variant}` : ""}` });
    setMiniCartOpen(true);
    setCart((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      return [
        ...prev,
        {
          key,
          slug: product.slug,
          name: product.name,
          color: product.color,
          price: product.price,
          image: product.images[0],
          variant,
          qty,
        },
      ];
    });
  }, []);

  const updateQty = useCallback((key, qty) => {
    setCart((prev) => (qty <= 0 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, qty } : i))));
  }, []);

  const removeFromCart = useCallback((key) => setCart((prev) => prev.filter((i) => i.key !== key)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    const exists = wishlist.some((p) => p.slug === product.slug);
    if (exists) {
      toast("Eliminat din favorite", { description: product.name });
      setWishlist(wishlist.filter((p) => p.slug !== product.slug));
      return;
    }
    toast.success("Salvat în favorite", { description: product.name });
    setWishlist([...wishlist, { slug: product.slug, name: product.name, color: product.color, price: product.price, image: product.images?.[0] || product.image, category: product.category }]);
  }, [wishlist]);

  const toggleCompare = useCallback((product) => {
    if (compare.some((p) => p.slug === product.slug)) {
      setCompare(compare.filter((p) => p.slug !== product.slug));
      return;
    }
    if (compare.length >= 3) {
      toast.error("Poți compara maximum 3 produse");
      return;
    }
    toast.success("Adăugat la comparare", { description: product.name });
    setCompare([...compare, { slug: product.slug, name: product.name, color: product.color, price: product.price, image: product.images?.[0] || product.image }]);
  }, [compare]);

  const value = useMemo(
    () => ({
      cart,
      cartCount: cart.reduce((s, i) => s + i.qty, 0),
      cartTotal: cart.reduce((s, i) => s + i.qty * i.price, 0),
      addToCart,
      updateQty,
      removeFromCart,
      clearCart,
      wishlist,
      toggleWishlist,
      isWishlisted: (slug) => wishlist.some((p) => p.slug === slug),
      compare,
      toggleCompare,
      clearCompare: () => setCompare([]),
      isCompared: (slug) => compare.some((p) => p.slug === slug),
      user,
      login: (email) => setUser({ email, name: email.split("@")[0] }),
      logout: () => setUser(null),
      miniCartOpen,
      setMiniCartOpen,
      searchOpen,
      setSearchOpen,
    }),
    [cart, wishlist, compare, user, miniCartOpen, searchOpen, addToCart, updateQty, removeFromCart, clearCart, toggleWishlist, toggleCompare]
  );

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};

export const useShop = () => useContext(ShopContext);
